import { useState, useEffect, useRef, useMemo } from "react";
import { useDebounce } from "use-debounce";
import { DepartureBoard } from "./DepartureBoard";
import { LinesPassingStop } from "./LinesPassingStop";
import {
  Spinner,
  AutocompleteSelect,
  Icon,
  Link,
  Box,
  Badge,
  Label,
  FieldDescription,
  Heading,
} from "@ogt/ui";

import { buildSpaUrl } from "src/config/urls";

interface Stop {
  PlaceName: string;
  OgtType: string;
  Ll: [number, number];
  Id: number;
  TaxeZone: {
    Id: number;
    Name: string;
    TypeAsString: string | null;
  };
}

export default function SingleStopSearch() {
  const apiBaseUrl =
    process.env.REACT_APP_TPS_API_BASE_URL?.replace(/\/$/, "") ?? "";
  const departureBoardBaseUrl =
    process.env.REACT_APP_DEPARTURE_BOARD_BASE_URL?.replace(/\/$/, "") ?? "";

  const isInitFromUrl = useRef(false);
  const [query, setQuery] = useState<string>("");
  const [results, setResults] = useState<Stop[]>([]);
  const [selectedStop, setSelectedStop] = useState<Stop | null>(null);
  const [debouncedQuery] = useDebounce(query, 300);
  const [isFocused, setIsFocused] = useState(false);
  const stopIdRef = useRef<number | null>(null);
  const [pendingStop, setPendingStop] = useState<Stop | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const [, setIsOpen] = useState<boolean>(false);
  const [isSelectionMade, setIsSelectionMade] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [resultLenght, setResultLenght] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [previousSearches, setPreviousSearches] = useState<Stop[]>(() => {
    try {
      const saved = localStorage.getItem("previousStops");
      return saved ? (JSON.parse(saved) as Stop[]) : [];
    } catch (e) {
      console.error("Error reading localStorage:", e);
      return [];
    }
  });
  const filteredResults = results.filter((stop) => stop.OgtType === "stop");
  const [closestStop] = useState<Stop | null>(null);

  const OPT_GET_CLOSEST = "__GET_CLOSEST__";
  const OPT_HEADER_PREV = "__HEADER_PREV__";
  const OPT_HEADER_RESULTS = "__HEADER_RESULTS__";

  function getDistance(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number {
    const toRad = (value: number) => (value * Math.PI) / 180;
    const R = 6371e3; // Earth radius in meters
    const φ1 = toRad(lat1);
    const φ2 = toRad(lat2);
    const Δφ = toRad(lat2 - lat1);
    const Δλ = toRad(lon2 - lon1);

    const a =
      Math.sin(Δφ / 2) ** 2 +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  }

  function getClosestStopByDistance(
    userLat: number,
    userLon: number,
    stops: Stop[]
  ): Stop | null {
    let closestStop: Stop | null = null;
    let minDistance = Infinity;

    for (const stop of stops) {
      if (!stop.Ll) continue;

      const [stopLat, stopLon] = stop.Ll;
      const distance = getDistance(userLat, userLon, stopLat, stopLon);

      if (distance < minDistance) {
        minDistance = distance;
        closestStop = stop;
      }
    }

    return closestStop;
  }

  // Fetches nearest stop based on browser geolocation
  const fetchClosestStop = () => {
    if (!navigator.geolocation) {
      alert("Geolocation stöds inte av din webbläsare.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const params = new URLSearchParams({
          q: "Link", // prevent empty q
          ll: `${latitude},${longitude}`,
          pointType: "stop",
        });

        const fullUrl = `${apiBaseUrl}/api/v1/StopAreas/Find?${params.toString()}`;

        fetch(fullUrl, {
          headers: { Accept: "application/json" },
        })
          .then((res) => res.json())
          .then((data: Stop[]) => {
            const filtered = data.filter((s) => s.OgtType === "stop");
            const closest = getClosestStopByDistance(
              latitude,
              longitude,
              filtered
            );

            if (closest) {
              handleSelect(closest);
            } else {
              alert("Ingen närliggande hållplats hittades.");
            }
          })

          .catch((err) => {
            console.error("Fel vid hämtning av närmaste hållplats:", err);
            alert("Kunde inte hämta närmaste hållplats.");
          });
      },
      (error) => {
        console.error("Geolocation error:", error);
        alert("Kunde inte få din position.");
      }
    );
  };

  useEffect(() => {
    const saved = localStorage.getItem("previousStops");
    if (saved) {
      try {
        const parsed = JSON.parse(saved) as Stop[];
        setPreviousSearches(parsed);
      } catch (e) {
        console.error("Could not parse previous searches from localStorage", e);
      }
    }
  }, []);

  // Persist previous searches in localStorage
  useEffect(() => {
    localStorage.setItem("previousStops", JSON.stringify(previousSearches));
  }, [previousSearches]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (stopIdRef.current !== null) {
      }
    }, 5000);

    return () => clearInterval(interval); // cleanup
  }, []);

  // Update screen reader status text
  useEffect(() => {
    const filtered = results.filter((stop) => stop.OgtType === "stop");

    if (filtered.length > 0) {
      setResultLenght("");
      setTimeout(() => {
        setResultLenght(
          `Det finns ${filtered.length} sökförslag och ${previousSearches.length} tidigare sökningar. Använd pilarna för att navigera.`
        );
      }, 100);
    }
  }, [results, previousSearches.length]);

  // Reset highlighted index when result set changes
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [results]);

  function decodeStopName(param: string) {
    return param.trim();
  }

  // Handles selection from autocomplete or URL init
  const handleSelect = (stop: Stop, commit = false) => {
    if (!stop) return;

    setIsSelectionMade(true);
    setIsSearching(false);
    setPendingStop(stop);
    setQuery(stop.PlaceName);
    setResults([]);
    setIsOpen(false);
    stopIdRef.current = stop.Id;

    if (commit) {
      setSelectedStop(stop);
    }

    setPreviousSearches((prev) => {
      const exists = prev.find((s) => s.Id === stop.Id);
      if (exists) return prev;
      return [stop, ...prev.slice(0, 4)];
    });

    const params = new URLSearchParams(window.location.search);
    params.set("stopId", String(stop.Id));
    params.set("stop", stop.PlaceName.trim());
    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}?${params.toString()}`
    );
  };

  const acWrapRef = useRef<HTMLDivElement>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (pendingStop) {
      setSelectedStop(pendingStop);

      setPreviousSearches((prev) => {
        const exists = prev.find((s) => s.Id === pendingStop.Id);
        if (exists) return prev;
        return [pendingStop, ...prev.slice(0, 4)];
      });
    }
  };

  // Returns badge/icon for stop type
  const getIcon = (stopType: string) => {
    switch (stopType) {
      case "address":
        return (
          <Badge
            variant="red"
            shape="default"
            state="default"
            icons={<Icon name="a" size={14} />}
          ></Badge>
        );
      case "stop":
        return (
          <Badge
            variant="red"
            shape="default"
            state="default"
            icons={<Icon name="h" size={14} />}
          ></Badge>
        );
      case "poi":
        return (
          <Badge
            variant="red"
            shape="default"
            state="default"
            icons={<Icon name="pin" size={14} />}
          ></Badge>
        );
      default:
        return null;
    }
  };

  function toStopDisplayId(stopId: number) {
    const suffix = stopId % 10000;
    return suffix === 0 ? stopId : suffix;
  }

  function buildStopMapHref(stop: Stop) {
    const stopId = String(toStopDisplayId(stop.Id));

    return buildSpaUrl("map", {
      aktivHallplats: stopId,
      hallplats: stopId,
      ll:
        stop.Ll?.length === 2
          ? `${stop.Ll[0].toFixed(6)},${stop.Ll[1].toFixed(6)}`
          : undefined,
      markerText: stop.PlaceName?.trim() || "",
    });
  }

  const mapUrl = selectedStop ? buildStopMapHref(selectedStop) : "";

  const slugify = (text: string) =>
    text
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9\s-]/g, "")
      .trim()
      .replace(/\s+/g, "-");

  const deptboardUrl =
    departureBoardBaseUrl + "/" + slugify(selectedStop?.PlaceName || "");

  const navigableItems: {
    stop: Stop;
    type: "result" | "previous" | "header" | "closest" | "get-closest";
  }[] = [];

  if (!isSearching) {
    const isQueryEmpty = query.trim() === "";

    if (isFocused && isQueryEmpty) {
      if (closestStop) {
        navigableItems.push({ stop: closestStop, type: "closest" });
      } else {
        navigableItems.push({
          stop: {
            Id: -999,
            PlaceName: "Min närmaste hållplats",
            OgtType: "stop",
            Ll: [0, 0],
            TaxeZone: { Id: 0, Name: "", TypeAsString: null },
          },
          type: "get-closest",
        });
      }
    }

    if (!isQueryEmpty) {
      filteredResults.forEach((stop) =>
        navigableItems.push({ stop, type: "result" })
      );
    }

    if (previousSearches.length > 0) {
      navigableItems.push({ stop: { Id: -1 } as Stop, type: "header" });

      previousSearches.forEach((stop) =>
        navigableItems.push({ stop, type: "previous" })
      );
    }
  }

  function stopToOption(stop: Stop) {
    return {
      value: String(stop.Id),
      label: stop.PlaceName,
      keywords: stop.PlaceName,
      leading: <Icon aria-hidden className="size-4 opacity-70" name="map" />,
    };
  }

  function normalizeStopName(name: string) {
    return name.replace(/,/g, "").replace(/\s+/g, " ").trim().toLowerCase();
  }

  const { options, indexMap } = useMemo(() => {
    const opts: Array<{
      value: string;
      label: string;
      disabled?: boolean;
      leading?: React.ReactNode;
      keywords?: string;
    }> = [];

    const idx = new Map<string, Stop>();
    const isQueryEmpty = query.trim() === "";

    const seen = new Set<number>();

    function pushStop(s: Stop, labelOverride?: string) {
      if (seen.has(s.Id)) return;
      const o = stopToOption(s);
      if (labelOverride) o.label = labelOverride;
      o.leading = <span>{getIcon(s.OgtType)}</span>;
      opts.push(o);
      idx.set(o.value, s);
      seen.add(s.Id);
    }

    if (isFocused && isQueryEmpty) {
      if (closestStop) {
        if (!seen.has(closestStop.Id)) {
          pushStop(
            closestStop,
            `Min närmaste hållplats – ${closestStop.PlaceName}`
          );
        }
      } else {
        opts.push({
          value: OPT_GET_CLOSEST,
          label: "Min närmaste hållplats",
          leading: (
            <Icon aria-hidden className="size-4 opacity-70" name="map" />
          ),
        });
      }
    }

    const hits = results.filter((s) => s.OgtType === "stop");
    const showHits = hits.length > 0 && !isQueryEmpty;
    if (showHits) {
      opts.push({
        value: OPT_HEADER_RESULTS,
        label: "Sökresultat",
        disabled: true,
      });
      hits.forEach((s) => pushStop(s));
    }

    const prevFiltered = previousSearches.filter((s) => !seen.has(s.Id));
    if (prevFiltered.length > 0) {
      opts.push({
        value: OPT_HEADER_PREV,
        label: "Tidigare sökningar",
        disabled: true,
      });
      prevFiltered.forEach((s) => pushStop(s));
    }

    return { options: opts, indexMap: idx };
  }, [isFocused, query, results, previousSearches, closestStop]);

  const inflight = useRef<AbortController | null>(null);

  // Initialize selected stop from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const stopIdParam = params.get("stopId");
    const stopParam = params.get("stop");

    async function initFromUrl() {
      if (stopIdParam && !Number.isNaN(Number(stopIdParam))) {
        const stopId = Number(stopIdParam);
        isInitFromUrl.current = true;

        try {
          const res = await fetch(`${apiBaseUrl}/api/v1/StopAreas/${stopId}`, {
            headers: { Accept: "application/json" },
          });

          if (!res.ok) {
            throw new Error(`Failed to fetch stop by id: ${res.status}`);
          }

          const data: Stop = await res.json();

          handleSelect(data, true);
          return;
        } catch (err) {
          console.warn(
            "[StopSearch] stopId lookup failed, fallback to stop name",
            err
          );
        }
      }

      if (stopParam) {
        isInitFromUrl.current = true;
        const decodedName = decodeStopName(stopParam);

        try {
          const searchParams = new URLSearchParams({
            q: decodedName,
            pointType: "stop",
          });

          const res = await fetch(
            `${apiBaseUrl}/api/v1/StopAreas/Find?${searchParams.toString()}`,
            {
              headers: { Accept: "application/json" },
            }
          );

          if (!res.ok) {
            throw new Error(`Failed to fetch stop by name: ${res.status}`);
          }

          const data: Stop[] = await res.json();

          const match = data.find(
            (s) =>
              normalizeStopName(s.PlaceName) === normalizeStopName(decodedName)
          );

          if (match) {
            handleSelect(match, true);
          } else {
            console.warn("[StopSearch] stop name lookup found no exact match", {
              decodedName,
              resultCount: data.length,
            });
          }
        } catch (err) {
          console.error("[StopSearch] stop name lookup failed", err);
        }
      }
    }

    initFromUrl();
  }, [apiBaseUrl]);

  useEffect(() => {
    if (isSelectionMade) return;

    const q = debouncedQuery.trim();

    if (q.length < 2) {
      setResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    inflight.current?.abort();

    const ctrl = new AbortController();
    inflight.current = ctrl;

    const params = new URLSearchParams({
      q,
      pointType: "stop",
    });

    fetch(`${apiBaseUrl}/api/v1/StopAreas/Find?${params.toString()}`, {
      signal: ctrl.signal,
      headers: { Accept: "application/json" },
    })
      .then((res) => res.json())
      .then((data: Stop[]) => {
        if (ctrl.signal.aborted) return;

        const filtered = (data || [])
          .filter((s) => s.OgtType === "stop")
          .slice(0, 30);

        setResults(filtered);
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          console.error("Fetch error:", err);
        }
      })
      .finally(() => {
        if (!ctrl.signal.aborted) {
          setIsSearching(false);
        }
      });

    return () => {
      ctrl.abort();
    };
  }, [debouncedQuery, isSelectionMade, apiBaseUrl]);

  return (
    <>
      <Box fullwidth noBorder className="mb-11">
        <Heading className="mb-4">När går min buss</Heading>
        <p>Välj den hållplats du vill åka ifrån för att se en avgångstavla.</p>

        <div className="single-stop-search">
          <div className="autocomplete-container gap-2 flex flex-col ">
            <form onSubmit={handleSubmit}>
              <div className="flex flex-col ">
                <Label htmlFor="stopSearch" className="mb-2">
                  Sök hållplats
                </Label>
                <FieldDescription
                  id="stopSearch-description"
                  className="mt-0! mb-2!"
                >
                  Börja skriv ett hållplatsnamn och välj sedan från listan.
                </FieldDescription>
                <div ref={acWrapRef}>
                  <AutocompleteSelect
                    Id="stopSearch"
                    aria-describedby="stopSearch-description"
                    className="w-full"
                    ref={inputRef}
                    openOnFocus={true}
                    emptyContent={
                      isSearching ? (
                        <div className="flex items-center gap-2 py-1">
                          <span>Söker hållplatser...</span>
                          <Spinner className="size-4" aria-hidden />
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 py-1">
                          Inga träffar.
                        </div>
                      )
                    }
                    options={options}
                    placeholder=""
                    selectionMode="manual"
                    showButton
                    buttonLabel="Visa avgångstavla"
                    buttonVariant="primary"
                    ButtonSize="large"
                    query={query}
                    onQueryChange={(q) => {
                      setQuery(q);
                      setIsSelectionMade(false);
                    }}
                    onSubmit={() => {
                      if (pendingStop) {
                        setSelectedStop(pendingStop);
                        setPreviousSearches((prev) => {
                          const exists = prev.find(
                            (s) => s.Id === pendingStop.Id
                          );
                          if (exists) return prev;
                          return [pendingStop, ...prev.slice(0, 4)];
                        });
                      } else {
                        const first = filteredResults[0];
                        if (first) setSelectedStop(first);
                      }
                    }}
                    onInputFocus={() => {
                      setIsFocused(true);

                      if (previousSearches.length > 0) {
                        setTimeout(() => {
                          setResultLenght(
                            `Det finns ${previousSearches.length} tidigare sökningar. Använd pilarna för att navigera.`
                          );
                        }, 100);
                      }
                    }}
                    onInputBlur={() => {
                      setTimeout(() => {
                        const next =
                          document.activeElement as HTMLElement | null;
                        if (next && acWrapRef.current?.contains(next)) return;

                        setIsFocused(false);
                        setResultLenght("");
                      }, 0);
                    }}
                    onValueChange={(val) => {
                      if (!val) return;
                      if (val === OPT_GET_CLOSEST) {
                        fetchClosestStop();
                        return;
                      }

                      const chosen = indexMap.get(val);
                      if (!chosen) return;

                      handleSelect(chosen);
                    }}
                  />
                </div>
              </div>
            </form>

            <div aria-live="polite" className="sr-only" role="status">
              {resultLenght}
            </div>

            <div aria-live="polite" className="sr-only" role="status">
              {highlightedIndex >= 0 &&
              highlightedIndex < navigableItems.length &&
              navigableItems[highlightedIndex].type !== "header"
                ? navigableItems[highlightedIndex].stop.PlaceName
                : ""}
            </div>

            <div aria-live="polite" className="sr-only" role="status">
              {statusMessage}
            </div>
          </div>
        </div>
      </Box>

      {selectedStop && (
        <div className="selected-stop mb-6">
          <Heading as="h3" className="flex items-center h2 mb-4 gap-2">
            {getIcon(selectedStop.OgtType)}
            <span className="sr-only">{selectedStop.OgtType}</span>
            {selectedStop.PlaceName}
          </Heading>
          {selectedStop?.TaxeZone?.Name && (
            <div>Kommun: {selectedStop.TaxeZone.Name}</div>
          )}
        </div>
      )}

      <div className="md:flex mb-6">
        {selectedStop && mapUrl && (
          <div className="md:mb-0 mb-4 md:mr-4">
            <Link
              href={mapUrl}
              target="_blank"
              format="box"
              boxVariant={"tertiary"}
              rel="noopener noreferrer"
              icon="external-link"
              iconPosition="right"
            >
              Visa hållplats på kartan
            </Link>
          </div>
        )}

        {selectedStop && deptboardUrl && (
          <div>
            <Link
              href={deptboardUrl}
              target="_blank"
              format="box"
              boxVariant={"tertiary"}
              rel="noopener noreferrer"
              icon="external-link"
              iconPosition="right"
            >
              Skapa egen avgångstavla
            </Link>
          </div>
        )}
      </div>

      <div className="mb-6">
        <DepartureBoard
          stopId={selectedStop?.Id!}
          stopName={selectedStop?.PlaceName!}
          onDepartureCountChange={(count) => {
            if (!selectedStop) return;

            setStatusMessage("");

            setTimeout(() => {
              setStatusMessage(
                count > 0
                  ? `Visar ${count} kommande avgångar från ${selectedStop.PlaceName}.`
                  : `Inga kommande avgångar från ${selectedStop.PlaceName}.`
              );
            }, 50);
          }}
        />
      </div>

      <div className="mb-6">
        <LinesPassingStop stopId={selectedStop?.Id!} />
      </div>
    </>
  );
}
